import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { prepareHeaders, handleApiError } from './helpers'
import { ISchemaAndId, ISchemaId } from '../../types/common'
import {
  PlaceResponse,
  PlacesResponse,
  PlaceUpdate,
  PlaceCreate,
} from '@backend/places/placesRouter'
import { SortOrdersBody } from '@backend/types'

export interface IPlace extends PlaceResponse {}

export interface IPlaces extends PlacesResponse {}

export interface IPlaceUpdate extends PlaceUpdate {}

export interface IPlaceCreate extends PlaceCreate {}

export interface ISortOrdersBody extends SortOrdersBody {}

export const placesApi = createApi({
  reducerPath: 'places',
  baseQuery: fetchBaseQuery({
    baseUrl: window.env.API_ENDPOINT,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    getPlaces: builder.query<IPlaces, ISchemaId>({
      query: ({ schemaId }) => ({
        url: `places/schema/${schemaId}`,
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.places.errors.getPlaces', err)
        }
      },
    }),
    addPlace: builder.query<void, IPlaceCreate & ISchemaId>({
      query: ({ name, schemaId }) => ({
        url: `places/schema/${schemaId}`,
        method: 'POST',
        body: {
          name: name.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.places.errors.addPlace', err)
        }
      },
    }),
    updatePlace: builder.query<void, IPlaceUpdate & ISchemaAndId>({
      query: ({ schemaId, id, name }) => ({
        url: `places/schema/${schemaId}/${id}`,
        method: 'PATCH',
        body: {
          name: name.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.places.errors.updatePlace', err)
        }
      },
    }),
    deletePlace: builder.query<void, ISchemaAndId>({
      query: ({ schemaId, id }) => ({
        url: `places/schema/${schemaId}/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.places.errors.deletePlace', err)
        }
      },
    }),
    updateSortOrder: builder.query<void, ISortOrdersBody & ISchemaId>({
      query: ({ schemaId, sortIds }) => ({
        url: `places/schema/${schemaId}/sort-order`,
        method: 'PATCH',
        body: {
          sortIds,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.places.errors.updateSortOrder', err)
        }
      },
    }),
  }),
})

export const {
  useLazyGetPlacesQuery,
  useLazyAddPlaceQuery,
  useLazyUpdatePlaceQuery,
  useLazyDeletePlaceQuery,
  useLazyUpdateSortOrderQuery,
} = placesApi
